import { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { useSession } from "users/__session/session-context";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material";
import { useGetAllOpenOfficerInvitationsQuery } from "legal-entities/invitations/officer-invitations/_api/entity-officer-invitation-api-service";
import EntityOfficerInvitationForm from "legal-entities/invitations/officer-invitations/_views/components/entity-officer-invitation-form";
import EntityOfficerInvitationsTable from "legal-entities/invitations/officer-invitations/_views/components/entity-officer-invitations-table";
import EntityOfficerInvitation from "legal-entities/invitations/officer-invitations/entity-officer-invitation";
import { useGetOnboardingWorkTemplateCatalogQuery } from "legal-entities/agreements/_api/entity-agreement-api-service";

const StyledEntitiesContainer = styled(Paper)(({ theme }) => ({
  [theme.breakpoints.down("xl")]: {
    gridTemplateColumns: "1fr",
    gridTemplateRows: "auto auto",
  },
  display: "grid",
  gridTemplateColumns: "1fr 2fr",
  gridTemplateRows: "1fr",
  padding: theme.spacing(1),
  position: "relative",
  overflowY: "auto",
  height: "100%",
  width: "100%",
}));

export default function EntitiesPage() {
  // Track only invitation identities locally. The list remains query-backed so changes made by
  // another superuser are not hidden by a stale local copy.
  const [sentThisVisitIds, setSentThisVisitIds] = useState<ReadonlySet<string>>(new Set());
  const session = useSession();
  const { data: returnedInvitations, error } = useGetAllOpenOfficerInvitationsQuery();
  const templatesQuery = useGetOnboardingWorkTemplateCatalogQuery();
  // Sending and cancelling both invalidate the open-invitation list, so the table renders the
  // query result directly. A local copy would go stale the moment another superuser changed one.
  const openInvitations = returnedInvitations ?? [];

  useEffect(() => {
    if (error) {
      enqueueSnackbar("Failed to get open invitations", { variant: "error" });
    }
  }, [error]);

  return (
    <StyledEntitiesContainer>
      <EntityOfficerInvitationForm
        templateCatalogRequired
        templates={templatesQuery.data?.map((template) => ({
          id: template.id,
          displayName: template.displayName,
          isDefault: template.isDefault,
          version: template.version,
          workflowVersion: template.workflowVersion,
          participationMode: template.participationMode,
          negotiable: template.negotiable,
        }))}
        templatesLoading={templatesQuery.isLoading}
        templatesError={templatesQuery.isError}
        onRetryTemplates={() => void templatesQuery.refetch()}
        onInvitationCreated={(invitation: EntityOfficerInvitation) => {
          const sentId = invitation.id?.value;
          if (sentId) setSentThisVisitIds((currentIds) => new Set(currentIds).add(sentId));
        }}
      />
      <EntityOfficerInvitationsTable
        session={session}
        invitations={openInvitations}
        sentThisVisitIds={sentThisVisitIds}
        onInvitationRemoved={(invitation) => {
          const removedId = invitation.id?.value;
          if (!removedId) return;

          setSentThisVisitIds((currentIds) => {
            if (!currentIds.has(removedId)) return currentIds;
            const remainingIds = new Set(currentIds);
            remainingIds.delete(removedId);
            return remainingIds;
          });
        }}
      />
    </StyledEntitiesContainer>
  );
}
